import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Trash2, FileDown, Mail, Plus, Sparkles, Loader2, RefreshCw, Star, Lock } from "lucide-react";
import api from "../lib/api";
import FederalPanel from "./FederalPanel";
import { StatusBadge, RFQ_STATUSES } from "./RFQs";
import { AuditBadge } from "./AuditLog";

function AuditTrail({ rfqId, rfq }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/admin/audit-log", { params: { rfq_id: rfqId, limit: 50 } }).then((r) => setItems(r.data.items)).catch(() => {});
  }, [rfqId, rfq]);
  return (
    <Card title="Audit Trail" testid="rfq-audit-card">
      {items.length === 0 ? <p className="text-slate-400 text-[13.5px]">No recorded activity yet.</p> : (
        <ul className="space-y-3">
          {items.map((e) => (
            <li key={e.id} className="text-[12.5px]" data-testid={`rfq-audit-entry-${e.id}`}>
              <div className="flex items-center justify-between gap-2">
                <AuditBadge action={e.action} />
                <span className="text-[11px] text-slate-400 whitespace-nowrap">{(e.created_at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
              <div className="mt-1 text-slate-600">{e.details}</div>
              <div className="text-[11px] text-slate-400">{e.actor}</div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function RFQDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rfq, setRfq] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [staff, setStaff] = useState([]);
  useEffect(() => { api.get("/admin/staff/lite").then((r) => setStaff(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    api.get(`/admin/rfqs/${id}`).then((r) => setRfq(r.data)).catch(() => toast.error("Failed to load RFQ"));
  }, [id]);

  useEffect(() => {
    load();
    api.get("/admin/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
  }, [load]);

  if (!rfq) return <div className="text-slate-500 p-8" data-testid="rfq-detail-loading">Loading RFQ...</div>;

  const patch = async (updates) => {
    try {
      const { data } = await api.patch(`/admin/rfqs/${id}`, updates);
      setRfq((r) => ({ ...r, ...data }));
      toast.success("Updated");
    } catch { toast.error("Update failed"); }
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await api.get(`/admin/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.original_filename || "file"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const info = [
    ["Contact", `${rfq.first_name} ${rfq.last_name}`], ["Company", rfq.company], ["Job Title", rfq.job_title],
    ["Email", rfq.email], ["Phone", rfq.phone], ["Industry", rfq.industry],
    ["Quantity", `${rfq.quantity} ${rfq.unit}`.trim()], ["Brand", rfq.brand], ["Part #", rfq.part_number],
    ["Delivery Location", rfq.delivery_location], ["Required Date", rfq.required_date], ["Budget", rfq.budget],
  ];

  return (
    <div data-testid="rfq-detail-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <Link to="/admin/rfqs" data-testid="rfq-back-link" className="text-slate-500 hover:text-[var(--pg-navy)]"><ArrowLeft size={20} /></Link>
          <div>
            <h1 className="font-[Poppins] font-bold text-[24px] text-[var(--pg-navy)]">{rfq.rfq_number}</h1>
            <p className="text-[13px] text-slate-500">{rfq.product_service}</p>
          </div>
          <StatusBadge status={rfq.status} />
          {rfq.requirement_type && rfq.requirement_type !== "Commercial Procurement" && (
            <span data-testid="rfq-type-chip" className="px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-bold bg-[#e6f1fb] text-[var(--pg-navy)]">{rfq.requirement_type.toUpperCase()}</span>
          )}
          {rfq.source === "reorder" && (
            <span data-testid="rfq-reorder-chip" className="px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-bold bg-purple-100 text-purple-700">
              REORDER{rfq.reorder_of ? ` of ${rfq.reorder_of}` : ""}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select data-testid="rfq-detail-status-select" className="pg-input !py-2.5" style={{ width: 200 }} value={rfq.status} onChange={(e) => patch({ status: e.target.value })}>
            {RFQ_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <select data-testid="rfq-priority-select" className="pg-input !py-2.5" style={{ width: 120 }} value={rfq.priority || "NORMAL"} onChange={(e) => patch({ priority: e.target.value })}>
            {["LOW", "NORMAL", "HIGH", "URGENT"].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <input data-testid="rfq-detail-assign-input" list="staff-emails" className="pg-input !py-2.5" style={{ width: 170 }} placeholder="Assign to..." defaultValue={rfq.assigned_to}
            onBlur={(e) => e.target.value !== rfq.assigned_to && patch({ assigned_to: e.target.value })} />
          <datalist id="staff-emails">{staff.map((u) => <option key={u.email} value={u.email}>{u.name}</option>)}</datalist>
          <input data-testid="rfq-supplier-deadline-input" type="date" title="Supplier response deadline" className="pg-input !py-2.5 !px-2 text-[12.5px]" style={{ width: 150 }} defaultValue={rfq.supplier_response_deadline || ""}
            onBlur={(e) => e.target.value !== (rfq.supplier_response_deadline || "") && patch({ supplier_response_deadline: e.target.value })} />
          {!confirmDelete ? (
            <button data-testid="rfq-delete-button" onClick={() => setConfirmDelete(true)} className="text-slate-400 hover:text-red-500 p-2" aria-label="Delete RFQ"><Trash2 size={17} /></button>
          ) : (
            <span className="flex items-center gap-2">
              <button data-testid="rfq-delete-confirm" onClick={async () => {
                try { await api.delete(`/admin/rfqs/${id}`); toast.success("RFQ and related records deleted"); navigate("/admin/rfqs"); }
                catch (err) { toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Delete failed"); setConfirmDelete(false); }
              }} className="text-[12px] font-[Poppins] font-semibold px-3 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700">Delete RFQ?</button>
              <button data-testid="rfq-delete-cancel" onClick={() => setConfirmDelete(false)} className="text-[12px] font-[Poppins] font-semibold text-slate-500 px-2">Cancel</button>
            </span>
          )}
        </div>
      </div>

      <div className="grid xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2 space-y-5">
          <Card title="Requirement" testid="rfq-requirement-card">
            <div className="grid sm:grid-cols-3 gap-x-6 gap-y-3 text-[13.5px]">
              {info.map(([k, v]) => (
                <div key={k}><div className="text-[11px] uppercase tracking-wider text-slate-500 font-[Poppins] font-semibold">{k}</div><div className="mt-0.5 font-medium text-[var(--pg-navy)]">{v || "—"}</div></div>
              ))}
            </div>
            {rfq.specifications && <Block label="Specifications" text={rfq.specifications} />}
            {rfq.additional_requirements && <Block label="Additional Requirements" text={rfq.additional_requirements} />}
          </Card>

          {(rfq.federal || rfq.federal_status || rfq.requirement_type === "Federal / Government Requirement" || rfq.requirement_type === "Prime Contractor RFQ") && <FederalPanel rfq={rfq} onChange={load} />}
          <AISupplierMatch rfq={rfq} onChange={load} />
          <SupplierRFQs rfq={rfq} suppliers={suppliers} onChange={load} />
          <SupplierQuotes rfq={rfq} suppliers={suppliers} onChange={load} />
          <Comparison rfqId={id} quotes={rfq.supplier_quotes || []} />
          <QuoteBuilder rfq={rfq} onChange={load} />
          <CustomerQuotes rfq={rfq} onChange={load} />
        </div>

        <div className="space-y-5">
          <Card title={`Documents (${(rfq.documents || []).length})`} testid="rfq-documents-card">
            {(rfq.documents || []).length === 0 && <p className="text-slate-400 text-[13.5px]">No documents uploaded.</p>}
            <ul className="space-y-2">
              {(rfq.documents || []).map((d) => (
                <li key={d.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 text-[13px]">
                  <span className="truncate mr-2">{d.original_filename}</span>
                  <button data-testid={`rfq-doc-download-${d.id}`} onClick={() => downloadDoc(d)} className="text-[var(--pg-accent)] hover:text-[var(--pg-navy)] p-1" aria-label="Download"><Download size={15} /></button>
                </li>
              ))}
            </ul>
          </Card>
          <Notes rfq={rfq} onChange={load} />
          <AuditTrail rfqId={id} rfq={rfq} />
        </div>
      </div>
    </div>
  );
}

function Card({ title, children, testid, action }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-[Poppins] font-bold text-[16px] text-[var(--pg-navy)]">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function Block({ label, text }) {
  return (
    <div className="mt-4 bg-slate-50 border-l-4 border-[var(--pg-accent)] rounded-md p-3">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-[Poppins] font-semibold">{label}</div>
      <p className="mt-1 text-[13.5px] whitespace-pre-wrap text-[var(--pg-navy)]">{text}</p>
    </div>
  );
}

function AISupplierMatch({ rfq, onChange }) {
  const [busy, setBusy] = useState(false);
  const result = rfq.ai_supplier_match;

  const run = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/rfqs/${rfq.id}/match-suppliers`);
      onChange();
      toast.success("AI supplier suggestions ready");
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "AI matching failed");
    } finally { setBusy(false); }
  };

  const scoreColor = (s) => s >= 75 ? "bg-green-100 text-green-700" : s >= 50 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500";

  return (
    <Card title="AI Supplier Match" testid="rfq-ai-match-card"
      action={
        <button data-testid="ai-match-button" onClick={run} disabled={busy}
          className="btn-primary !py-2 !px-3.5 text-[12.5px] disabled:opacity-70">
          {busy ? <Loader2 className="animate-spin" size={14} /> : result ? <RefreshCw size={14} /> : <Sparkles size={14} />}
          {busy ? "Analyzing..." : result ? "Re-run" : "Suggest Suppliers"}
        </button>
      }>
      {!result && !busy && <p className="text-slate-400 text-[13.5px]">Let AI rank your supplier directory against this RFQ's product, specs and requirements.</p>}
      {busy && !result && <p className="text-slate-400 text-[13.5px]">Comparing this RFQ against your supplier directory...</p>}
      {result && (
        <div data-testid="ai-match-results">
          {result.summary && (
            <p className="text-[13.5px] text-[var(--pg-navy)] bg-slate-50 border-l-4 border-[var(--pg-accent)] rounded-md p-3" data-testid="ai-match-summary">
              <Sparkles size={13} className="inline mr-1.5 text-[var(--pg-accent)]" />{result.summary}
            </p>
          )}
          {(result.matches || []).length === 0 ? (
            <p className="mt-3 text-slate-400 text-[13.5px]">No strong supplier fits found in your directory for this request.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {result.matches.map((m, i) => (
                <li key={m.supplier_id} data-testid={`ai-match-row-${m.supplier_id}`} className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 rounded-lg px-4 py-2.5 text-[13px]">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="font-[Poppins] font-bold text-slate-400 text-[12px]">#{i + 1}</span>
                    <Link to={`/admin/suppliers?q=${encodeURIComponent(m.company_name)}`} className="font-medium text-[var(--pg-navy)] hover:text-[var(--pg-accent)]">{m.company_name}</Link>
                    {m.supplier_status && <span className="text-[10.5px] font-[Poppins] font-semibold text-slate-400 uppercase">{m.supplier_status}</span>}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="text-[12.5px] text-slate-500 max-w-[420px]">{m.reason}</span>
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-bold shrink-0 ${scoreColor(m.score)}`}>{m.score}% fit</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {result.generated_at && <p className="mt-3 text-[11px] text-slate-400">Generated {(result.generated_at || "").slice(0, 16).replace("T", " ")} UTC</p>}
        </div>
      )}
    </Card>
  );
}

function Notes({ rfq, onChange }) {
  const [content, setContent] = useState("");
  const add = async () => {
    if (!content.trim()) return;
    try {
      await api.post(`/admin/rfqs/${rfq.id}/notes`, { content });
      setContent(""); onChange();
    } catch { toast.error("Failed to add note"); }
  };
  return (
    <Card title={`Notes (${(rfq.notes || []).length})`} testid="rfq-notes-card">
      <div className="flex gap-2">
        <input data-testid="rfq-note-input" className="pg-input !py-2.5 flex-1" placeholder="Add internal note..." value={content} onChange={(e) => setContent(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
        <button data-testid="rfq-note-add-button" onClick={add} className="btn-primary !py-2.5 !px-4"><Plus size={16} /></button>
      </div>
      <ul className="mt-4 space-y-3">
        {[...(rfq.notes || [])].reverse().map((n) => (
          <li key={n.id} className="bg-slate-50 rounded-lg p-3 text-[13px]">
            <p className="text-[var(--pg-navy)] whitespace-pre-wrap">{n.content}</p>
            <div className="mt-1.5 text-[11.5px] text-slate-400">{n.author} · {(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function SupplierRFQs({ rfq, suppliers, onChange }) {
  const [show, setShow] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [f, setF] = useState({ quote_deadline: "", message: "" });
  const approved = suppliers.filter((s) => ["ACTIVE", "PREFERRED"].includes(s.status));
  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const send = async () => {
    if (selected.size === 0) { toast.error("Select at least one approved supplier"); return; }
    try {
      const { data } = await api.post(`/admin/rfqs/${rfq.id}/supplier-rfqs/bulk`, { supplier_ids: [...selected], ...f });
      if (data.sent.length) toast.success(`RFQ sent to ${data.sent.join(", ")}`);
      (data.skipped || []).forEach((s) => toast.error(`${s.supplier || s.supplier_id}: ${s.reason}`));
      setShow(false); setSelected(new Set()); setF({ quote_deadline: "", message: "" });
      onChange();
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Send failed");
    }
  };
  const cancel = async (s) => {
    if (!window.confirm(`Cancel the RFQ sent to ${s.supplier_name}?`)) return;
    try { await api.post(`/admin/supplier-rfqs/${s.id}/cancel`); toast.success("Supplier RFQ cancelled"); onChange(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Cancel failed"); }
  };
  const badge = {
    PENDING: ["SENT", "bg-blue-100 text-blue-700"], VIEWED: ["VIEWED", "bg-cyan-100 text-cyan-700"],
    QUOTED: ["QUOTE SUBMITTED", "bg-green-100 text-green-700"], DECLINED: ["DECLINED", "bg-slate-200 text-slate-500"],
    EXPIRED: ["EXPIRED", "bg-red-100 text-red-600"], CANCELLED: ["CANCELLED", "bg-slate-200 text-slate-400"],
  };
  return (
    <Card title={`Supplier RFQs Sent (${(rfq.supplier_rfqs || []).length})`} testid="rfq-supplier-rfqs-card"
      action={<button data-testid="send-supplier-rfq-button" onClick={() => setShow(!show)} className="btn-primary !py-2 !px-3.5 text-[12.5px]"><Plus size={14} /> Send to Suppliers</button>}>
      {show && (
        <div className="mb-4 bg-slate-50 rounded-xl p-4" data-testid="supplier-rfq-form">
          <label className="pg-label">Approved Suppliers * <span className="normal-case text-slate-400">(only Active/Preferred are eligible)</span></label>
          {approved.length === 0 ? <p className="text-[13px] text-amber-700 mt-1">No approved suppliers yet — approve suppliers first.</p> : (
            <div className="mt-1.5 grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-[180px] overflow-y-auto">
              {approved.map((s) => (
                <label key={s.id} className={`flex items-center gap-2 text-[13px] rounded-lg border px-3 py-2 cursor-pointer ${selected.has(s.id) ? "border-[var(--pg-accent)] bg-blue-50" : "border-slate-200 bg-white"}`}>
                  <input data-testid={`srfq-supplier-check-${s.id}`} type="checkbox" className="accent-[var(--pg-accent)]" checked={selected.has(s.id)} onChange={() => toggle(s.id)} />
                  <span className="truncate">{s.company_name}</span>
                  {s.status === "PREFERRED" && <Star size={11} className="text-blue-500 flex-shrink-0" />}
                </label>
              ))}
            </div>
          )}
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            <div><label className="pg-label">Quote Deadline</label><input data-testid="srfq-deadline-input" type="date" className="pg-input !py-2.5" value={f.quote_deadline} onChange={(e) => setF({ ...f, quote_deadline: e.target.value })} /></div>
            <div><label className="pg-label">Message</label><input data-testid="srfq-message-input" className="pg-input !py-2.5" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Optional note to suppliers" /></div>
          </div>
          <button data-testid="srfq-send-button" onClick={send} className="btn-primary mt-3 !py-2.5 !px-5 text-[13px]">
            Send RFQ to {selected.size || ""} Supplier{selected.size === 1 ? "" : "s"}
          </button>
        </div>
      )}
      {(rfq.supplier_rfqs || []).length === 0 ? <p className="text-slate-400 text-[13.5px]">No supplier RFQs sent yet. Approved suppliers respond directly in the supplier portal.</p> : (
        <ul className="space-y-2">
          {rfq.supplier_rfqs.map((s) => {
            const [label, cls] = badge[s.status] || [s.status, "bg-slate-100 text-slate-500"];
            return (
              <li key={s.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-slate-50 rounded-lg px-4 py-2.5 text-[13px]" data-testid={`srfq-row-${s.id}`}>
                <span className="font-medium text-[var(--pg-navy)]">{s.supplier_name}</span>
                <span className="text-slate-500">Sent {(s.created_at || "").slice(0, 10)}</span>
                {s.quote_deadline && <span className="text-slate-500">Deadline {s.quote_deadline}</span>}
                {s.viewed_at && <span className="text-cyan-700 text-[12px]">Viewed {(s.viewed_at || "").slice(0, 10)}</span>}
                {s.quoted_at && <span className="text-green-700 text-[12px]">Responded {(s.quoted_at || "").slice(0, 10)}</span>}
                {s.quote_total != null && <span className="font-semibold text-[var(--pg-navy)]">{money(s.quote_total)}</span>}
                {s.decline_reason && <span className="text-slate-400 italic text-[12px]">"{s.decline_reason}"</span>}
                <span className={`ml-auto px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-semibold ${cls}`}>{label}</span>
                {["PENDING", "VIEWED"].includes(s.status) && (
                  <button data-testid={`srfq-cancel-${s.id}`} onClick={() => cancel(s)} title="Cancel this supplier RFQ"
                    className="text-slate-400 hover:text-red-500 text-[12px] font-[Poppins] font-semibold">Cancel</button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function SupplierQuotes({ rfq, suppliers, onChange }) {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ supplier_id: "", unit_price: "", quantity: "", lead_time_days: "", availability: "", part_number: "", manufacturer: "", payment_terms: "", supplier_quote_number: "", shipping_cost: "", freight: "", insurance: "", duties: "", handling: "", other_costs: "", warranty: "", expires_at: "", notes: "" });
  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.supplier_id || !form.unit_price || !form.quantity) { toast.error("Supplier, unit price and quantity are required"); return; }
    try {
      await api.post(`/admin/rfqs/${rfq.id}/supplier-quotes`, {
        ...form,
        unit_price: parseFloat(form.unit_price), quantity: parseFloat(form.quantity),
        lead_time_days: parseInt(form.lead_time_days || 0, 10),
        shipping_cost: parseFloat(form.shipping_cost || 0), freight: parseFloat(form.freight || 0),
        insurance: parseFloat(form.insurance || 0), duties: parseFloat(form.duties || 0),
        handling: parseFloat(form.handling || 0), other_costs: parseFloat(form.other_costs || 0),
      });
      toast.success("Supplier quote added");
      setShow(false);
      setForm({ supplier_id: "", unit_price: "", quantity: "", lead_time_days: "", availability: "", part_number: "", manufacturer: "", payment_terms: "", supplier_quote_number: "", shipping_cost: "", freight: "", insurance: "", duties: "", handling: "", other_costs: "", warranty: "", expires_at: "", notes: "" });
      onChange();
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Failed to add quote");
    }
  };

  const remove = async (qid) => {
    try { await api.delete(`/admin/supplier-quotes/${qid}`); onChange(); } catch { toast.error("Delete failed"); }
  };

  return (
    <Card title={`Supplier Quotes (${(rfq.supplier_quotes || []).length})`} testid="rfq-supplier-quotes-card"
      action={<button data-testid="add-supplier-quote-button" onClick={() => setShow(!show)} className="btn-primary !py-2 !px-3.5 text-[12.5px]"><Plus size={14} /> Add Quote</button>}>
      {show && (
        <div className="mb-5 bg-slate-50 rounded-xl p-4 grid sm:grid-cols-3 gap-3" data-testid="supplier-quote-form">
          <div className="sm:col-span-3">
            <label className="pg-label">Supplier *</label>
            <select data-testid="sq-supplier-select" className="pg-input !py-2.5" value={form.supplier_id} onChange={handle("supplier_id")}>
              <option value="">Select supplier...</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.company_name} ({s.status})</option>)}
            </select>
            {suppliers.length === 0 && <p className="mt-1 text-[12px] text-amber-600">No suppliers yet — add one in the Suppliers tab.</p>}
          </div>
          <div><label className="pg-label">Unit Price ($) *</label><input data-testid="sq-unit-price-input" type="number" step="0.01" className="pg-input !py-2.5" value={form.unit_price} onChange={handle("unit_price")} /></div>
          <div><label className="pg-label">Quantity *</label><input data-testid="sq-quantity-input" type="number" step="1" className="pg-input !py-2.5" value={form.quantity} onChange={handle("quantity")} /></div>
          <div><label className="pg-label">Lead Time (days)</label><input data-testid="sq-lead-time-input" type="number" className="pg-input !py-2.5" value={form.lead_time_days} onChange={handle("lead_time_days")} /></div>
          <div><label className="pg-label">Availability</label><input data-testid="sq-availability-input" placeholder="e.g. In stock, 2 wks ARO" className="pg-input !py-2.5" value={form.availability} onChange={handle("availability")} /></div>
          <div><label className="pg-label">Part Number</label><input data-testid="sq-part-number-input" className="pg-input !py-2.5" value={form.part_number} onChange={handle("part_number")} /></div>
          <div><label className="pg-label">Manufacturer</label><input data-testid="sq-manufacturer-input" className="pg-input !py-2.5" value={form.manufacturer} onChange={handle("manufacturer")} /></div>
          <div><label className="pg-label">Payment Terms</label><input data-testid="sq-payment-terms-input" placeholder="e.g. Net 30" className="pg-input !py-2.5" value={form.payment_terms} onChange={handle("payment_terms")} /></div>
          <div><label className="pg-label">Supplier Quote #</label><input data-testid="sq-supplier-quote-number-input" className="pg-input !py-2.5" value={form.supplier_quote_number} onChange={handle("supplier_quote_number")} /></div>
          <div><label className="pg-label">Shipping ($)</label><input data-testid="sq-shipping-input" type="number" step="0.01" className="pg-input !py-2.5" value={form.shipping_cost} onChange={handle("shipping_cost")} /></div>
          <div><label className="pg-label">Freight ($)</label><input data-testid="sq-freight-input" type="number" step="0.01" className="pg-input !py-2.5" value={form.freight} onChange={handle("freight")} /></div>
          <div><label className="pg-label">Insurance ($)</label><input data-testid="sq-insurance-input" type="number" step="0.01" className="pg-input !py-2.5" value={form.insurance} onChange={handle("insurance")} /></div>
          <div><label className="pg-label">Duties ($)</label><input data-testid="sq-duties-input" type="number" step="0.01" className="pg-input !py-2.5" value={form.duties} onChange={handle("duties")} /></div>
          <div><label className="pg-label">Handling ($)</label><input data-testid="sq-handling-input" type="number" step="0.01" className="pg-input !py-2.5" value={form.handling} onChange={handle("handling")} /></div>
          <div><label className="pg-label">Other Costs ($)</label><input data-testid="sq-other-input" type="number" step="0.01" className="pg-input !py-2.5" value={form.other_costs} onChange={handle("other_costs")} /></div>
          <div><label className="pg-label">Quote Expiration</label><input data-testid="sq-expires-input" type="date" className="pg-input !py-2.5" value={form.expires_at} onChange={handle("expires_at")} /></div>
          <div className="sm:col-span-2"><label className="pg-label">Warranty / Notes</label><input data-testid="sq-warranty-input" className="pg-input !py-2.5" value={form.warranty} onChange={handle("warranty")} /></div>
          <div className="sm:col-span-3">
            <button data-testid="sq-save-button" onClick={submit} className="btn-primary !py-2.5 !px-5 text-[13px]">Save Supplier Quote</button>
          </div>
        </div>
      )}
      {(rfq.supplier_quotes || []).length === 0 ? <p className="text-slate-400 text-[13.5px]">No supplier quotes entered yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] min-w-[560px]">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3">Supplier</th><th className="py-2 pr-3">Unit</th><th className="py-2 pr-3">Qty</th><th className="py-2 pr-3">Lead</th><th className="py-2 pr-3">Terms</th><th className="py-2 pr-3">Expires</th><th className="py-2 pr-3">Total</th><th></th>
            </tr></thead>
            <tbody>
              {rfq.supplier_quotes.map((q) => {
                const totals = rfq.supplier_quotes.map((x) => x.total_landed_cost || 0);
                const isLowest = rfq.supplier_quotes.length > 1 && (q.total_landed_cost || 0) === Math.min(...totals);
                return (
                <tr key={q.id} className={`border-b border-slate-100 ${q.preferred ? "bg-green-50/60" : isLowest ? "bg-amber-50/50" : ""}`}>
                  <td className="py-2.5 pr-3 font-medium text-[var(--pg-navy)]">
                    {q.supplier_name}
                    {isLowest && <span data-testid={`sq-lowest-${q.id}`} className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-[Poppins] font-bold">LOWEST COST</span>}
                    {q.preferred && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-[Poppins] font-bold">PREFERRED</span>}
                    {(q.part_number || q.manufacturer || q.supplier_quote_number) && (
                      <div className="text-[11px] font-normal text-slate-400 mt-0.5">
                        {[q.part_number && `PN ${q.part_number}`, q.manufacturer, q.supplier_quote_number && `Ref ${q.supplier_quote_number}`].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </td>
                  <td className="py-2.5 pr-3">{money(q.unit_price)}</td>
                  <td className="py-2.5 pr-3">{q.quantity}</td>
                  <td className="py-2.5 pr-3">{q.lead_time_days}d</td>
                  <td className="py-2.5 pr-3 text-slate-500">{q.payment_terms || "—"}</td>
                  <td className="py-2.5 pr-3 text-slate-500">{q.expires_at || "—"}</td>
                  <td className="py-2.5 pr-3 font-semibold">{money(q.total_landed_cost)}</td>
                  <td className="py-2.5 text-right whitespace-nowrap">
                    {!q.preferred && (
                      <button data-testid={`sq-prefer-${q.id}`} title="Select this supplier (records who, when and why)"
                        onClick={async () => {
                          const reason = window.prompt(`Select ${q.supplier_name} as the supplier for this RFQ.\nSelection reason / internal note (optional):`, "");
                          if (reason === null) return;
                          try { await api.post(`/admin/rfqs/${rfq.id}/supplier-quotes/${q.id}/prefer`, { reason }); toast.success(`${q.supplier_name} selected as supplier`); onChange(); } catch { toast.error("Could not select supplier"); }
                        }}
                        className="text-slate-400 hover:text-green-600 p-1" aria-label="Prefer"><Star size={14} /></button>
                    )}
                    <button data-testid={`sq-delete-${q.id}`} onClick={() => remove(q.id)} className="text-slate-400 hover:text-red-500 p-1" aria-label="Delete"><Trash2 size={14} /></button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          {(() => {
            const sel = rfq.supplier_quotes.find((q) => q.preferred && q.selected_by);
            return sel ? (
              <p data-testid="sq-selection-info" className="mt-2 text-[12px] text-green-700">
                Selected: <strong>{sel.supplier_name}</strong> by {sel.selected_by} on {(sel.selected_at || "").slice(0, 10)}
                {sel.selection_reason ? <span className="text-slate-500 italic"> — "{sel.selection_reason}"</span> : null}
              </p>
            ) : null;
          })()}
        </div>
      )}
    </Card>
  );
}

function Comparison({ rfqId, quotes }) {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  useEffect(() => {
    if (quotes.length > 1) {
      api.get(`/admin/rfqs/${rfqId}/comparison`).then((r) => setData(r.data)).catch(() => {});
    } else setData(null);
  }, [rfqId, quotes.length]);

  const generateSummary = async (force = false) => {
    setAiBusy(true);
    try {
      const { data: s } = await api.post(`/admin/rfqs/${rfqId}/comparison-summary${force ? "?force=true" : ""}`);
      setSummary(s);
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "AI summary failed");
    } finally { setAiBusy(false); }
  };

  if (!data || (data.quotes || []).length < 2) return null;
  return (
    <Card title="Supplier Comparison" testid="rfq-comparison-card"
      action={
        <button data-testid="ai-summary-button" onClick={() => generateSummary(Boolean(summary))} disabled={aiBusy}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3.5 py-2 rounded-full bg-[var(--pg-navy)] text-white hover:bg-[var(--pg-accent)] transition-colors disabled:opacity-70">
          {aiBusy ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}
          {aiBusy ? "Analyzing..." : summary ? "Regenerate" : "AI Recommendation"}
        </button>
      }>
      {summary && (
        <div data-testid="ai-summary-panel" className="mb-5 bg-gradient-to-br from-blue-50 to-slate-50 border border-blue-100 rounded-xl p-4">
          <div className="flex items-center gap-2 text-[12px] font-[Poppins] font-bold uppercase tracking-wider text-[var(--pg-accent)]">
            <Sparkles size={13} /> AI Sourcing Recommendation
          </div>
          <p data-testid="ai-summary-recommendation" className="mt-2 text-[13.5px] text-[var(--pg-navy)] leading-relaxed">{summary.recommendation}</p>
          {(summary.key_points || []).length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {summary.key_points.map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-[12.5px] text-slate-600">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[var(--pg-accent)] flex-shrink-0" />{p}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-2.5 text-[11px] text-slate-400">Generated {(summary.generated_at || "").slice(0, 10)} · AI-assisted — verify before committing</div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-[13px] min-w-[560px]">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="py-2 pr-3">Supplier</th><th className="py-2 pr-3">Unit Price</th><th className="py-2 pr-3">Qty</th><th className="py-2 pr-3">Availability</th><th className="py-2 pr-3">Lead Time</th><th className="py-2 pr-3">Freight+Other</th><th className="py-2 pr-3">Total Landed</th>
          </tr></thead>
          <tbody>
            {data.quotes.map((q) => (
              <tr key={q.id} className={`border-b border-slate-100 ${q.id === data.best_quote_id ? "bg-green-50" : ""}`} data-testid={`comparison-row-${q.id}`}>
                <td className="py-2.5 pr-3 font-medium text-[var(--pg-navy)]">{q.supplier_name}{q.id === data.best_quote_id && <span className="ml-2 text-[10.5px] font-[Poppins] font-bold text-green-600 uppercase">Best</span>}</td>
                <td className="py-2.5 pr-3">{money(q.unit_price)}</td>
                <td className="py-2.5 pr-3">{q.quantity}</td>
                <td className="py-2.5 pr-3 text-slate-500">{q.availability || "—"}</td>
                <td className="py-2.5 pr-3">{q.lead_time_days} days</td>
                <td className="py-2.5 pr-3">{money((q.shipping_cost || 0) + (q.freight || 0) + (q.insurance || 0) + (q.duties || 0) + (q.handling || 0) + (q.other_costs || 0))}</td>
                <td className="py-2.5 pr-3 font-bold">{money(q.total_landed_cost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function QuoteBuilder({ rfq, onChange }) {
  const [show, setShow] = useState(false);
  const [marginMode, setMarginMode] = useState("percent");
  const [threshold, setThreshold] = useState(10);
  const [f, setF] = useState({
    product: rfq.product_service || "", description: "", quantity: rfq.quantity && !isNaN(parseFloat(rfq.quantity)) ? rfq.quantity : "1",
    unit_cost: "", freight: "", insurance: "", duties: "", handling: "", other_costs: "",
    margin_percent: "20", margin_amount: "", shipping_charge: "0", tax: "0", lead_time: "", payment_terms: "Net 30", valid_days: "30", warranty: "", terms: "",
  });
  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      const t = parseFloat(r.data?.low_margin_threshold);
      if (!isNaN(t)) setThreshold(t);
    }).catch(() => {});
  }, []);
  const h = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const n = (v) => parseFloat(v) || 0;
  const optNum = (v) => (String(v).trim() === "" ? null : n(v));

  const qty = n(f.quantity) || 1;
  const landed = n(f.unit_cost) * qty + n(f.freight) + n(f.insurance) + n(f.duties) + n(f.handling) + n(f.other_costs);
  const subtotal = marginMode === "amount" && String(f.margin_amount).trim() !== ""
    ? landed + n(f.margin_amount)
    : landed * (1 + n(f.margin_percent) / 100);
  const total = subtotal + n(f.shipping_charge) + n(f.tax);
  const profit = subtotal - landed;
  const marginPct = subtotal ? (profit / subtotal) * 100 : 0;
  const lowMargin = subtotal > 0 && marginPct < threshold;

  const applySupplierQuote = (q) => {
    setF((x) => ({ ...x, unit_cost: String(q.unit_price), quantity: String(q.quantity), freight: String((q.shipping_cost || 0) + (q.freight || 0)), insurance: String(q.insurance || 0), duties: String(q.duties || 0), handling: String(q.handling || 0), other_costs: String(q.other_costs || 0), lead_time: q.lead_time_days ? `${q.lead_time_days} days` : x.lead_time }));
    setShow(true);
  };

  const create = async () => {
    if (!f.product.trim() || !n(f.quantity) || n(f.unit_cost) < 0) { toast.error("Product, quantity and unit cost required"); return; }
    try {
      await api.post(`/admin/rfqs/${rfq.id}/customer-quotes`, {
        ...f, quantity: n(f.quantity), unit_cost: n(f.unit_cost),
        freight: optNum(f.freight), insurance: optNum(f.insurance),
        duties: optNum(f.duties), handling: optNum(f.handling), other_costs: optNum(f.other_costs),
        margin_percent: n(f.margin_percent),
        margin_amount: marginMode === "amount" && String(f.margin_amount).trim() !== "" ? n(f.margin_amount) : null,
        shipping_charge: n(f.shipping_charge), tax: n(f.tax), valid_days: parseInt(f.valid_days || 30, 10),
      });
      toast.success("Customer quote created");
      setShow(false); onChange();
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Failed to create quote");
    }
  };

  return (
    <Card title="Margin Calculator & Customer Quote" testid="rfq-quote-builder-card"
      action={<button data-testid="quote-builder-toggle" onClick={() => setShow(!show)} className="btn-primary !py-2 !px-3.5 text-[12.5px]"><Plus size={14} /> New Quote</button>}>
      {(rfq.supplier_quotes || []).length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {rfq.supplier_quotes.map((q) => (
            <button key={q.id} data-testid={`use-supplier-quote-${q.id}`} onClick={() => applySupplierQuote(q)}
              className="text-[12px] font-[Poppins] font-semibold px-3 py-1.5 rounded-full bg-slate-100 hover:bg-[var(--pg-accent)] hover:text-white transition-colors">
              Use {q.supplier_name} ({money(q.unit_price)}/unit)
            </button>
          ))}
        </div>
      )}
      {!show ? <p className="text-slate-400 text-[13.5px]">Open the builder to calculate landed cost, margin, and generate a quote.</p> : (
        <div data-testid="quote-builder-form">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2"><label className="pg-label">Product *</label><input data-testid="qb-product-input" className="pg-input !py-2.5" value={f.product} onChange={h("product")} /></div>
            <div><label className="pg-label">Quantity *</label><input data-testid="qb-quantity-input" type="number" className="pg-input !py-2.5" value={f.quantity} onChange={h("quantity")} /></div>
            <div className="sm:col-span-3"><label className="pg-label">Description</label><input data-testid="qb-description-input" className="pg-input !py-2.5" value={f.description} onChange={h("description")} /></div>
            <div><label className="pg-label">Unit Cost ($) *</label><input data-testid="qb-unit-cost-input" type="number" step="0.01" className="pg-input !py-2.5" value={f.unit_cost} onChange={h("unit_cost")} /></div>
            <div><label className="pg-label">Freight ($)</label><input data-testid="qb-freight-input" type="number" step="0.01" placeholder="Not provided" className="pg-input !py-2.5" value={f.freight} onChange={h("freight")} /></div>
            <div><label className="pg-label">Insurance ($)</label><input data-testid="qb-insurance-input" type="number" step="0.01" placeholder="Not provided" className="pg-input !py-2.5" value={f.insurance} onChange={h("insurance")} /></div>
            <div><label className="pg-label">Duties / Taxes ($)</label><input data-testid="qb-duties-input" type="number" step="0.01" placeholder="Not provided" className="pg-input !py-2.5" value={f.duties} onChange={h("duties")} /></div>
            <div><label className="pg-label">Handling ($)</label><input data-testid="qb-handling-input" type="number" step="0.01" placeholder="Not provided" className="pg-input !py-2.5" value={f.handling} onChange={h("handling")} /></div>
            <div><label className="pg-label">Other ($)</label><input data-testid="qb-other-input" type="number" step="0.01" placeholder="Not provided" className="pg-input !py-2.5" value={f.other_costs} onChange={h("other_costs")} /></div>
            <div className="sm:col-span-3 flex items-center gap-4 text-[12.5px] font-[Poppins] font-semibold text-slate-600">
              Pricing mode:
              <label className="flex items-center gap-1.5 cursor-pointer"><input data-testid="qb-mode-percent" type="radio" checked={marginMode === "percent"} onChange={() => setMarginMode("percent")} /> Margin %</label>
              <label className="flex items-center gap-1.5 cursor-pointer"><input data-testid="qb-mode-amount" type="radio" checked={marginMode === "amount"} onChange={() => setMarginMode("amount")} /> Fixed profit $</label>
            </div>
            {marginMode === "percent" ? (
              <>
                <div><label className="pg-label">Markup %</label><input data-testid="qb-margin-input" type="number" step="0.5" className="pg-input !py-2.5" value={f.margin_percent} onChange={h("margin_percent")} /></div>
                <div><label className="pg-label">Target GM % <span className="text-slate-400 normal-case">(auto-sets markup)</span></label>
                  <input data-testid="qb-target-gm-input" type="number" step="0.5" min="0" max="95" className="pg-input !py-2.5" placeholder="e.g. 17.5"
                    onChange={(e) => { const gm = parseFloat(e.target.value); if (!isNaN(gm) && gm > 0 && gm < 100) setF((x) => ({ ...x, margin_percent: ((gm / (100 - gm)) * 100).toFixed(2) })); }} />
                </div>
              </>
            ) : (
              <div><label className="pg-label">Fixed Gross Profit ($)</label><input data-testid="qb-margin-amount-input" type="number" step="0.01" className="pg-input !py-2.5" value={f.margin_amount} onChange={h("margin_amount")} /></div>
            )}
            <div><label className="pg-label">Shipping to Customer ($)</label><input data-testid="qb-shipping-charge-input" type="number" step="0.01" className="pg-input !py-2.5" value={f.shipping_charge} onChange={h("shipping_charge")} /></div>
            <div><label className="pg-label">Sales Tax ($)</label><input data-testid="qb-tax-input" type="number" step="0.01" className="pg-input !py-2.5" value={f.tax} onChange={h("tax")} /></div>
            <div><label className="pg-label">Lead Time</label><input data-testid="qb-lead-time-input" className="pg-input !py-2.5" value={f.lead_time} onChange={h("lead_time")} /></div>
            <div><label className="pg-label">Payment Terms</label><input data-testid="qb-payment-terms-input" className="pg-input !py-2.5" value={f.payment_terms} onChange={h("payment_terms")} /></div>
            <div><label className="pg-label">Valid (days)</label><input data-testid="qb-valid-days-input" type="number" className="pg-input !py-2.5" value={f.valid_days} onChange={h("valid_days")} /></div>
            <div className="sm:col-span-3"><label className="pg-label">Warranty / Terms</label><input data-testid="qb-terms-input" className="pg-input !py-2.5" value={f.terms} onChange={h("terms")} /></div>
          </div>
          {lowMargin && (
            <div data-testid="qb-low-margin-warning" className="mt-4 flex items-center gap-2 bg-amber-50 border border-amber-300 rounded-lg px-4 py-2.5 text-[13px] text-amber-800 font-medium">
              ⚠ Margin Review Required — gross margin {marginPct.toFixed(1)}% is below your {threshold}% threshold. You can still create the quote.
            </div>
          )}
          <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-3 bg-[var(--pg-navy)] rounded-xl p-4 text-white" data-testid="qb-financial-summary">
            <Fin label="Landed Cost" value={money(landed)} testid="qb-landed-cost" />
            <Fin label="Customer Subtotal" value={money(subtotal)} testid="qb-subtotal" />
            <Fin label="Quote Total" value={money(total)} testid="qb-total" accent />
            <Fin label="Gross Profit" value={money(profit)} testid="qb-gross-profit" />
            <Fin label="Gross Margin" value={`${marginPct.toFixed(1)}%`} testid="qb-gross-margin" />
          </div>
          <button data-testid="qb-create-quote-button" onClick={create} className="btn-primary mt-4 !py-2.5 !px-5 text-[13px]">Create Customer Quote</button>
        </div>
      )}
    </Card>
  );
}

function Fin({ label, value, testid, accent }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-white/60 font-[Poppins] font-semibold">{label}</div>
      <div className={`mt-1 font-[Poppins] font-bold text-[16px] ${accent ? "text-[var(--pg-accent)]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function CustomerQuotes({ rfq, onChange }) {
  const quotes = rfq.customer_quotes || [];
  if (quotes.length === 0) return null;

  const download = async (q) => {
    try {
      const res = await api.get(`/admin/customer-quotes/${q.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${q.quote_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); }
  };

  const send = async (q) => {
    try {
      const { data } = await api.post(`/admin/customer-quotes/${q.id}/send`);
      toast[data.email_sent ? "success" : "warning"](data.message);
      onChange();
    } catch { toast.error("Send failed"); }
  };

  const setStatus = async (q, status) => {
    try { await api.patch(`/admin/customer-quotes/${q.id}`, { status }); toast.success(`Quote ${status.toLowerCase()}`); onChange(); }
    catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Update failed");
    }
  };

  return (
    <Card title={`Customer Quotes (${quotes.length})`} testid="rfq-customer-quotes-card">
      <div className="space-y-3">
        {quotes.map((q) => (
          <div key={q.id} data-testid={`customer-quote-${q.quote_number}`} className="border border-slate-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-[Poppins] font-bold text-[15px] text-[var(--pg-navy)]">
                {q.quote_number} <StatusBadge status={q.status} />
                {q.status === "ACCEPTED" && (
                  <span data-testid={`quote-locked-${q.id}`} title="Accepted quotes are locked — only a super admin can modify them"
                    className="ml-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-[Poppins] font-semibold bg-slate-100 text-slate-500 border border-slate-200 align-middle">
                    <Lock size={10} /> LOCKED
                  </span>
                )}
              </div>
              <div className="text-[12.5px] text-slate-500 mt-1">{q.product} · Total {money(q.total)} · Margin {q.gross_margin_percent}%</div>
              <QuoteEngagement q={q} />
            </div>
            <div className="flex flex-wrap gap-2">
              <button data-testid={`quote-pdf-${q.id}`} onClick={() => download(q)} className="inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200"><FileDown size={14} /> PDF</button>
              <button data-testid={`quote-send-${q.id}`} onClick={() => send(q)} className="inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3 py-2 rounded-lg bg-[var(--pg-accent)] text-white hover:bg-[var(--pg-navy)]"><Mail size={14} /> Email Customer</button>
              {q.status !== "ACCEPTED" && <button data-testid={`quote-accept-${q.id}`} onClick={() => setStatus(q, "ACCEPTED")} className="text-[12.5px] font-[Poppins] font-semibold px-3 py-2 rounded-lg bg-green-100 text-green-700 hover:bg-green-200">Mark Accepted</button>}
              {q.status !== "DECLINED" && q.status !== "ACCEPTED" && <button data-testid={`quote-decline-${q.id}`} onClick={() => setStatus(q, "DECLINED")} className="text-[12.5px] font-[Poppins] font-semibold px-3 py-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200">Declined</button>}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function QuoteEngagement({ q }) {
  const chips = [];
  if (q.sent_at) chips.push(["sent", `Sent ${q.sent_at.slice(0, 10)}`, "bg-blue-50 text-blue-700 border-blue-200"]);
  if (q.first_viewed_at) chips.push(["viewed", `Viewed ×${q.view_count || 1} · first ${q.first_viewed_at.slice(0, 10)}`, "bg-cyan-50 text-cyan-700 border-cyan-200"]);
  if (q.accepted_at) chips.push(["accepted", `Accepted ${q.accepted_at.slice(0, 10)}${q.accepted_by ? ` by ${q.accepted_by}` : ""}${q.accepted_via ? ` (${q.accepted_via})` : ""}`, "bg-green-50 text-green-700 border-green-200"]);
  if (q.declined_at) chips.push(["declined", `Declined ${q.declined_at.slice(0, 10)}${q.declined_reason ? ` — "${q.declined_reason}"` : ""}`, "bg-red-50 text-red-600 border-red-200"]);
  if (chips.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`quote-engagement-${q.id}`}>
      {chips.map(([key, text, cls]) => (
        <span key={key} className={`text-[11px] font-medium px-2 py-0.5 rounded-md border ${cls}`}>{text}</span>
      ))}
    </div>
  );
}
